/**
 * Popsie Fish Co — Tier Picker Integration
 * Bridges custom tier cards with Dawn's native variant-selects pipeline.
 * Uses event delegation so clicks survive Dawn's section re-rendering.
 *
 * Flow: click tier card → update hidden <select> → dispatch change →
 * Dawn fetches new section HTML → price/media/availability update.
 *
 * Accessibility: ARIA radiogroup with roving tabindex, keyboard navigation
 * (Arrow keys, Enter, Space), and prefers-reduced-motion support.
 */
(function () {
  'use strict';

  // Inline icons for the nudge chip (swapped when the box hits the 20 lb max).
  var NUDGE_ICON_UP = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>';
  var NUDGE_ICON_CHECK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>';

  /**
   * Update the PDP progress banner dynamically based on the selected tier's weight
   * @param {Element} picker - The .popsie-tier-picker container
   * @param {number} selectedWeight - The weight in lbs of the selected tier
   */
  function updateProgressBanner(picker, selectedWeight) {
    var banner = picker.querySelector('.popsie-pdp-cart-progress-banner');
    if (!banner) return;

    var cartWeight = parseFloat(banner.dataset.cartWeight || 0);
    var totalPotential = cartWeight + selectedWeight;
    var cartPct = Math.min((cartWeight / 20) * 100, 100);
    var potentialPct = Math.min((totalPotential / 20) * 100, 100);

    // Grow the solid fill to the projected total (so the bar visibly fills
    // as larger tiers are selected)
    var fill = banner.querySelector('.popsie-progress-fill');
    if (fill) {
      fill.style.width = potentialPct + '%';
    }

    // Shade the portion the selected tier adds (cart -> projected)
    var preview = banner.querySelector('.popsie-progress-preview');
    if (preview) {
      preview.style.left = cartPct + '%';
      preview.style.width = Math.max(potentialPct - cartPct, 0) + '%';
    }

    // Light up milestone markers the projected box weight reaches
    banner.querySelectorAll('.popsie-progress-mark').forEach(function (mark) {
      var lb = parseFloat(mark.dataset.lb || 0);
      mark.classList.toggle('reached', totalPotential >= lb);
    });

    // Update the nudge: final milestone state, chip icon, and message
    var isMax = totalPotential >= 20;
    var nudge = banner.querySelector('.popsie-progress-banner-nudge');
    if (nudge) {
      nudge.classList.toggle('is-max', isMax);
      var chip = nudge.querySelector('.popsie-progress-nudge-chip');
      if (chip) chip.innerHTML = isMax ? NUDGE_ICON_CHECK : NUDGE_ICON_UP;
    }

    var nudgeText = banner.querySelector('.popsie-pdp-nudge-text');
    if (nudgeText) {
      var html = '';
      if (totalPotential < 5) {
        html = 'Adding this ' + selectedWeight + ' lb brings your box to <strong>' + totalPotential + ' lbs</strong>. Just <strong>' + (5 - totalPotential) + ' lb</strong> more to reach the 5 lb Starter box milestone.';
      } else if (totalPotential < 10) {
        html = 'Adding this ' + selectedWeight + ' lb brings your box to <strong>' + totalPotential + ' lbs</strong>. Just <strong>' + (10 - totalPotential) + ' lb</strong> more to reach the 10 lb Value box milestone.';
      } else if (totalPotential < 15) {
        html = 'Adding this ' + selectedWeight + ' lb brings your box to <strong>' + totalPotential + ' lbs</strong>. Just <strong>' + (15 - totalPotential) + ' lb</strong> more to reach the 15 lb Family box milestone.';
      } else if (totalPotential < 20) {
        html = 'Adding this ' + selectedWeight + ' lb brings your box to <strong>' + totalPotential + ' lbs</strong>. Just <strong>' + (20 - totalPotential) + ' lb</strong> more to reach the 20 lb Freezer box milestone.';
      } else {
        html = 'Adding this ' + selectedWeight + ' lb fills your box to <strong>' + totalPotential + ' lbs</strong>. Your box is stocked past the 20 lb Freezer milestone.';
      }
      nudgeText.innerHTML = html;
    }
  }

  /**
   * Write the chosen variant id straight into the add-to-cart form(s).
   *
   * Dawn only updates the hidden `id` input after a network round-trip
   * (select change -> section fetch -> updateVariantInputs). If that trip is
   * slow, aborted, or throws, the card the customer sees ("20 lb") and the
   * variant the form submits ("5 lb") silently diverge. Writing the id here,
   * synchronously, on every selection closes that gap; the fetch then only
   * refreshes price/media and (re)confirms the same id.
   * @param {string} sectionId
   * @param {string} variantId
   */
  function syncFormVariant(sectionId, variantId) {
    if (!variantId) return;
    ['product-form-', 'product-form-installment-'].forEach(function (prefix) {
      var form = document.getElementById(prefix + sectionId);
      if (!form) return;
      var input = form.querySelector('input[name="id"]');
      if (!input) return;
      input.disabled = false;
      if (input.value !== variantId) {
        input.value = variantId;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  /**
   * Mark exactly one <option> as selected — both the live property and the
   * `selected` attribute. Dawn's VariantSelects reads the ATTRIBUTE, and its
   * change handler throws if no option carries it, so we always leave the
   * select in a consistent state before dispatching `change`.
   */
  function setSelectedOption(select, index) {
    for (var k = 0; k < select.options.length; k++) {
      select.options[k].removeAttribute('selected');
      select.options[k].selected = false;
    }
    var chosen = select.options[index];
    chosen.setAttribute('selected', 'selected');
    chosen.selected = true;
    select.selectedIndex = index;
  }

  /**
   * Select a tier card: update visual state, ARIA attributes, hidden
   * <select>, and trigger Dawn's variant pipeline.
   * @param {Element} card - The .popsie-tier-card to activate
   * @param {Object} [opts] - Options
   * @param {boolean} [opts.focus] - Whether to move focus to the card
   */
  function selectTierCard(card, opts) {
    opts = opts || {};

    if (!card) return;
    if (card.classList.contains('sold-out')) return;

    var picker = card.closest('.popsie-tier-picker');
    if (!picker) return;

    var sectionId = picker.dataset.sectionId;
    var cards = picker.querySelectorAll('.popsie-tier-card');

    // Update visual state and ARIA attributes (roving tabindex)
    cards.forEach(function (c) {
      c.classList.remove('active');
      c.setAttribute('aria-checked', 'false');
      c.setAttribute('tabindex', '-1');
    });
    card.classList.add('active');
    card.setAttribute('aria-checked', 'true');
    card.setAttribute('tabindex', '0');

    // Get weight and update progress banner
    var weight = parseFloat(card.dataset.weight || 0);
    updateProgressBanner(picker, weight);

    // Move focus if requested (keyboard navigation)
    if (opts.focus) {
      card.focus();
    }

    // Check the hidden radio
    var radio = card.querySelector('input[type="radio"]');
    if (radio) radio.checked = true;

    // The form must carry this card's variant id from this moment on —
    // independent of whether Dawn's section fetch below ever completes.
    syncFormVariant(sectionId, card.dataset.variantId);

    // Re-query the hidden select each time (it may have been replaced by Dawn)
    var hiddenSelect = document.querySelector(
      '#variant-selects-' + sectionId + ' select'
    );
    if (!hiddenSelect) return;

    // Try matching by option-value-id first (most reliable)
    var optionValueId = card.dataset.optionValueId;
    var matched = false;

    if (optionValueId) {
      for (var i = 0; i < hiddenSelect.options.length; i++) {
        var opt = hiddenSelect.options[i];
        if (opt.dataset.optionValueId === optionValueId) {
          setSelectedOption(hiddenSelect, i);
          matched = true;
          break;
        }
      }
    }

    // Fallback: match by variant title text (exact, or "5 lb" vs "5 lbs")
    if (!matched) {
      var weightEl = card.querySelector('.popsie-tier-weight');
      if (!weightEl) return;
      var weightText = weightEl.textContent.trim().replace(/\s+/g, ' ');

      for (var j = 0; j < hiddenSelect.options.length; j++) {
        var optText = hiddenSelect.options[j].textContent.trim().replace(/\s+/g, ' ');
        if (optText === weightText || optText.replace(/lbs\b/g, 'lb') === weightText) {
          setSelectedOption(hiddenSelect, j);
          matched = true;
          break;
        }
      }
    }

    // Trigger Dawn's native change event to kick off the variant pipeline
    if (matched) {
      hiddenSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  // --- Click handler (event delegation — survives Dawn's AJAX section re-renders) ---
  document.addEventListener('click', function (e) {
    // A click on the <label> card also fires a synthetic click on its hidden
    // radio; handle the card once, not twice (each pass kicks off a fetch).
    if (e.target.tagName === 'INPUT') return;
    var card = e.target.closest('.popsie-tier-card');
    if (!card) return;

    selectTierCard(card);
  });

  // --- Keyboard handler (roving tabindex pattern for radiogroup) ---
  document.addEventListener('keydown', function (e) {
    var card = e.target.closest('.popsie-tier-card');
    if (!card) return;

    var picker = card.closest('.popsie-tier-picker');
    if (!picker) return;

    var key = e.key;
    var isArrow = (key === 'ArrowLeft' || key === 'ArrowUp' ||
                   key === 'ArrowRight' || key === 'ArrowDown');
    var isActivate = (key === 'Enter' || key === ' ');

    if (!isArrow && !isActivate) return;

    // Prevent page scrolling on arrow keys and space
    e.preventDefault();

    if (isActivate) {
      // Enter/Space: confirm selection (same as click)
      selectTierCard(card, { focus: true });
      return;
    }

    // Arrow keys: move to previous/next available (non-sold-out) card
    var cards = Array.prototype.slice.call(
      picker.querySelectorAll('.popsie-tier-card')
    );
    var currentIndex = cards.indexOf(card);
    if (currentIndex === -1) return;

    var direction = (key === 'ArrowRight' || key === 'ArrowDown') ? 1 : -1;
    var len = cards.length;
    var nextIndex = currentIndex;

    // Walk in direction, wrapping around, skipping sold-out cards
    for (var step = 0; step < len; step++) {
      nextIndex = (nextIndex + direction + len) % len;
      if (!cards[nextIndex].classList.contains('sold-out')) {
        selectTierCard(cards[nextIndex], { focus: true });
        return;
      }
    }
  });

  // Initialize progress banners on DOMContentLoaded and on section load
  function initAllProgressBanners() {
    document.querySelectorAll('.popsie-tier-picker').forEach(function (picker) {
      var activeCard = picker.querySelector('.popsie-tier-card.active');

      // Reconcile a click that landed before this script ran (the <label>
      // still checks its radio natively) or a browser-restored radio state:
      // the checked radio is the customer's latest choice, so honour it.
      var checkedRadio = picker.querySelector('.popsie-tier-card input[type="radio"]:checked');
      var checkedCard = checkedRadio ? checkedRadio.closest('.popsie-tier-card') : null;
      if (checkedCard && checkedCard !== activeCard && !checkedCard.classList.contains('sold-out')) {
        selectTierCard(checkedCard);
        return;
      }

      if (activeCard) {
        var weight = parseFloat(activeCard.dataset.weight || 0);
        updateProgressBanner(picker, weight);
        // Make sure the form already carries the visibly selected variant.
        syncFormVariant(picker.dataset.sectionId, activeCard.dataset.variantId);
      }
    });
  }

  // Run on initial load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllProgressBanners);
  } else {
    initAllProgressBanners();
  }

  // Also support custom section loads (Shopify theme editor, ajax page loads)
  document.addEventListener('shopify:section:load', initAllProgressBanners);
})();
